import React, { useEffect, useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { SimpleDialog } from "./components";
import { registerEvent } from "./electron-modules";
import { useAuth, useDialog } from "./hooks";
import { SortOption } from "./types/search";
import stc from "string-to-color";
import { getSortOptions, useItemsSearch } from "./hooks/use-item-search";
import { asyncTimeout, clearWindowHash, loadProjectJSON, toLocaleString } from "./utilities";
import { toast } from "./app";

const ProjectListRow = (props) => {
  const {project, onClose} = props;

  const {i18n, t} = useTranslation();
  const {getConfig} = useAuth();
  const theme = useTheme();

  const onClick = async () => {
    onClose();
    toast.success(t("toast.load-cloud-file"));
    await asyncTimeout(250);

    const projectJSON = await getConfig(project.id);
    clearWindowHash();
    loadProjectJSON(projectJSON);
  };

  const dateString = toLocaleString(project.timeCreated, i18n.language);
  const accentColor = stc(project.name);

  return (
    <ListItem
      button
      onClick={onClick}
      key={project.id}
      style={{
        margin: theme.spacing(1, 0),
        border: "1px solid #F2F4F7",
        borderLeft: `5px solid ${accentColor}`,
      }}
    >
      <ListItemText
        primary={`${project.name}`}
        secondary={t('project-list.created_on', {
          date: dateString
        })}
      />
    </ListItem>
  );
};

export const CloudProjectList = () => {
  const {t} = useTranslation();
  const {logIn, isLoggedIn, getProjects} = useAuth();
  const projectDialog = useDialog();
  const theme = useTheme();

  const [userProjects, setUserProjects] = useState([]);
  const itemsSearch = useItemsSearch();
  const sortOptions = getSortOptions();

  const getUserProjects = async () => {
    const projects = await getProjects();
    setUserProjects(projects);
  };

  const onSearchChange = event => {
    const query = event.target.value;
    itemsSearch.handleSearchChange(query);
  };

  const onSortChange = event => {
    const {sortDir, sortBy} = JSON.parse(event.target.value);
    itemsSearch.handleSortChange(sortDir, sortBy);
  };

  useEffect(() => {
    registerEvent("open-cloud-projects", async () => {
      if (!isLoggedIn) {
        const success = await logIn();
        if (!success) return;
      }

      projectDialog.handleOpen();
    });
  }, [isLoggedIn]);

  useEffect(() => {
    if (!projectDialog.open) return;
    getUserProjects();
  }, [projectDialog.open]);

  let projects = [...userProjects];
  projects = itemsSearch.handleSearch(projects);
  projects = itemsSearch.handleSort(projects);

  const hasProjects = projects.length > 0;

  const sortValue = JSON.stringify({
    sortDir: itemsSearch.state.sortDir,
    sortBy: itemsSearch.state.sortBy
  });

  return (
    <SimpleDialog
      open={projectDialog.open}
      onClose={projectDialog.handleClose}
      title={t('project-list.title')}
    >
      <React.Fragment>
        <Box style={{
          display: "flex"
        }}>

          {/** Search text */}
          <TextField
            type="search"
            label={t('project-list.search_projects')}
            onChange={onSearchChange}
            value={itemsSearch.state.query}
            style={{
              flex: 2,
              margin: theme.spacing(1)
            }}
          />

          {/** Sort by dropdown */}
          <TextField
            select
            label={t('project-list.sort_by')}
            onChange={onSortChange}
            value={sortValue}
            variant="standard"
            style={{
              flex: 1,
              margin: theme.spacing(1)
            }}
          >
            {sortOptions.map((option: SortOption, index) => (
              <MenuItem
                key={index}
                value={JSON.stringify(option.value)}
              >
                {option.label}
              </MenuItem >
            ))}
          </TextField>

        </Box>

        {!hasProjects && <React.Fragment>
          <Box style={{
            padding: theme.spacing(2),
            marginTop: theme.spacing(3),
            textAlign: "center"
          }}>
            <Typography color="textSecondary">
              {t('project-list.no_projects_found')}
            </Typography>
          </Box>
        </React.Fragment>}

        {hasProjects && (
          <List>
            {projects.map(project =>
              <ProjectListRow
                key={project.id}
                project={project}
                onClose={projectDialog.handleClose}
              />
            )}
          </List>
        )}

      </React.Fragment>

    </SimpleDialog>
  );
};
